import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import SearchIcon from "@mui/icons-material/Search";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CheckIcon from "@mui/icons-material/Check";

import {
    fetchOrgAddressBookEntries,
    copyOrgEntriesToBook,
} from "js/Api/address_book";
import { ClassificationBadge } from "./ClassificationSelect";

// ─── helpers ──────────────────────────────

function initialsColor(name) {
    const hue = ((name || "X").charCodeAt(0) * 13) % 360;
    return `hsl(${hue},50%,52%)`;
}

function initials(name) {
    return (name || "?")
        .split(" ")
        .slice(0, 2)
        .map((w) => w[0])
        .join("")
        .toUpperCase();
}

// The system-default book is presented to users as the org "Contact List".
function isDefaultBook(b) {
    return b?.bookType === "system_default" || b?.systemKey === "default";
}
function bookDisplayName(b) {
    return isDefaultBook(b) ? "Contact List" : b?.name;
}

// ─── BookSelector  ─────────────────────────────────────────

function BookSelector({ books, value, onChange }) {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const selected = books.find((b) => b.id === value);

    // Close on outside click
    useEffect(() => {
        const handler = (e) => {
            if (ref.current && !ref.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div ref={ref} style={{ position: "relative", flex: "1 1 auto", minWidth: 0 }}>
            {/* Trigger button */}
            <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    padding: "10px 14px",
                    border: `1.5px solid ${open ? "#1976d2" : "#e0e0e0"}`,
                    borderRadius: 8,
                    background: "#fff",
                    cursor: "pointer",
                    fontSize: 14,
                    color: "#1a1a2e",
                    transition: "border-color 0.15s",
                    textAlign: "left",
                }}
            >
                <span style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {selected ? bookDisplayName(selected) : "Select a book"}
                </span>
                <ExpandMoreIcon
                    sx={{
                        fontSize: "1.2rem",
                        color: "#aaa",
                        flexShrink: 0,
                        transform: open ? "rotate(180deg)" : "none",
                        transition: "transform 0.15s",
                    }}
                />
            </button>

            {/* Dropdown */}
            {open && (
                <div style={{
                    position: "absolute",
                    top: "calc(100% + 4px)",
                    left: 0,
                    right: 0,
                    background: "#fff",
                    border: "1.5px solid #e0e0e0",
                    borderRadius: 8,
                    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                    zIndex: 100,
                    overflow: "hidden",
                }}>
                    {books.map((b) => (
                        <button
                            key={b.id}
                            type="button"
                            onClick={() => { onChange(b.id); setOpen(false); }}
                            style={{
                                width: "100%",
                                display: "flex",
                                alignItems: "center",
                                gap: 10,
                                padding: "10px 14px",
                                border: "none",
                                background: b.id === value ? "#e8f0fe" : "#fff",
                                color: b.id === value ? "#1976d2" : "#1a1a2e",
                                cursor: "pointer",
                                fontSize: 14,
                                textAlign: "left",
                                transition: "background 0.1s",
                            }}
                            onMouseEnter={(e) => { if (b.id !== value) e.currentTarget.style.background = "#f5f5f5"; }}
                            onMouseLeave={(e) => { e.currentTarget.style.background = b.id === value ? "#e8f0fe" : "#fff"; }}
                        >
                            <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: 500 }}>
                                {bookDisplayName(b)}
                            </span>
                            <span style={{ fontSize: 12, color: "#999", flexShrink: 0 }}>
                                {b.entryCount ?? 0}
                            </span>
                            {b.id === value && <CheckIcon sx={{ fontSize: "1rem", color: "#1976d2", flexShrink: 0 }} />}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── ContactCard ────────────────────

function ContactCard({ entry, checked, onToggle }) {
    const [hovered, setHovered] = useState(false);
    const bg = checked ? "#eef3ff" : hovered ? "#f7f9ff" : "#fff";
    const border = checked ? "#93b4f5" : hovered ? "#d0dff7" : "#f0f0f0";
    const avatarBg = initialsColor(entry.displayName);
    const initial = initials(entry.displayName);
    const avatarSrc = entry.avatarUrl
        ? `${entry.avatarUrl}${entry.avatarUpdatedAt ? `?v=${encodeURIComponent(entry.avatarUpdatedAt)}` : ""}`
        : null;

    return (
        <div
            onClick={onToggle}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 14px",
                background: bg,
                border: `1px solid ${border}`,
                borderRadius: 10,
                cursor: "pointer",
                transition: "all 0.12s",
                userSelect: "none",
            }}
        >
            {/* Checkbox */}
            <div
                style={{
                    width: 20, height: 20, borderRadius: 6, flexShrink: 0,
                    border: `2px solid ${checked ? "#1976d2" : "#ccc"}`,
                    background: checked ? "#1976d2" : "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    transition: "all 0.12s",
                }}
            >
                {checked && <CheckIcon sx={{ fontSize: "0.85rem", color: "#fff" }} />}
            </div>

            {/* Avatar */}
            {avatarSrc ? (
                <img
                    src={avatarSrc}
                    alt={entry.displayName || ""}
                    style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                />
            ) : (
                <div style={{
                    width: 36, height: 36, borderRadius: "50%", flexShrink: 0,
                    background: avatarBg, color: "#fff",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 13, fontWeight: 700,
                }}>
                    {initial}
                </div>
            )}

            {/* Info */}
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: "#1a1a2e", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {entry.displayName || "—"}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 4, marginTop: 2 }}>
                    <PhoneOutlinedIcon sx={{ fontSize: "0.72rem", color: "#1976d2" }} />
                    <span style={{ fontSize: 12, color: "#777", fontFamily: "monospace" }}>
                        {entry.phoneNumberRaw || "—"}
                    </span>
                </div>
            </div>
        </div>
    );
}

// ─── Main modal──────────────────────────────────────

function CopyContactsModal({ targetBook, sourceBooks, onClose, onDone }) {
    const [selectedSourceId, setSelectedSourceId] = useState(sourceBooks[0]?.id || "");
    const [sourceEntries, setSourceEntries] = useState([]);
    const [loadingEntries, setLoadingEntries] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState("");
    const [copying, setCopying] = useState(false);

    // ── load entries whenever source book changes ────────────────────────────

    const loadSourceEntries = useCallback(async (bookId) => {
        if (!bookId) return;
        setLoadingEntries(true);
        setSelectedIds(new Set());
        try {
            const res = await fetchOrgAddressBookEntries(bookId);
            if (res.success) {
                setSourceEntries(
                    (Array.isArray(res.data) ? res.data : []).filter(
                        (e) => e.entryType === "phone_contact"
                    )
                );
            } else {
                toast.error(res.error || "Failed to load contacts");
                setSourceEntries([]);
            }
        } finally {
            setLoadingEntries(false);
        }
    }, []);

    useEffect(() => { loadSourceEntries(selectedSourceId); }, [selectedSourceId, loadSourceEntries]);

    const handleSourceChange = (bookId) => {
        setSelectedSourceId(bookId);
        setSearchQuery("");
    };

    // ── filtering ────────────────────────────────────────────────────────────

    const filteredEntries = useMemo(() => {
        const q = searchQuery.trim().toLowerCase();
        const list = !q
            ? sourceEntries
            : sourceEntries.filter(
                (e) =>
                    (e.displayName || "").toLowerCase().includes(q) ||
                    (e.phoneNumberRaw || "").toLowerCase().includes(q)
            );
        return [...list].sort((a, b) =>
            (a.displayName || "").localeCompare(b.displayName || "", undefined, { sensitivity: "base" })
        );
    }, [sourceEntries, searchQuery]);

    // ── select helpers ───────────────────────────────────────────────────────

    const toggleEntry = (id) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    };

    const allChecked = filteredEntries.length > 0 && filteredEntries.every((e) => selectedIds.has(e.id));
    const someChecked = selectedIds.size > 0 && !allChecked;

    // Warn when selected contacts carry a different classification than the
    // target book. The copy backend resets copied entries to the book default,
    // so the user should review/adjust them afterward.
    const targetClass = targetBook.classification || "safe";
    const mismatchCount = useMemo(() => {
        return sourceEntries.filter(
            (e) => selectedIds.has(e.id) && (e.classification || "safe") !== targetClass
        ).length;
    }, [sourceEntries, selectedIds, targetClass]);

    const toggleAll = () => {
        if (allChecked) {
            setSelectedIds(new Set());
        } else {
            setSelectedIds(new Set(filteredEntries.map((e) => e.id)));
        }
    };

    // ── copy ─────────────────────────────────────────────────────────────────

    const handleCopy = async () => {
        const toCopy = sourceEntries.filter((e) => selectedIds.has(e.id));
        if (toCopy.length === 0) {
            toast.error("Select at least one contact to copy.");
            return;
        }
        setCopying(true);
        const res = await copyOrgEntriesToBook(targetBook.id, toCopy);
        setCopying(false);
        if (res.success) {
            toast.success(res.message || "Contacts copied");
            onDone();
            onClose();
        } else {
            toast.error(res.error || "Failed to copy contacts");
        }
    };

    // ── keyboard shortcut ────────────────────────────────────────────────────

    useEffect(() => {
        const onKey = (e) => { if (e.key === "Escape" && !copying) onClose(); };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [copying, onClose]);

    // ── render ───────────────────────────────────────────────────────────────

    return (
        <div
            style={{
                position: "fixed", inset: 0,
                background: "rgba(0,0,0,0.45)",
                zIndex: 1050,
                display: "flex", alignItems: "center", justifyContent: "center",
            }}
            onClick={() => !copying && onClose()}
        >
            <div
                style={{
                    background: "#fff",
                    borderRadius: 16,
                    width: 560,
                    maxWidth: "94vw",
                    maxHeight: "88vh",
                    display: "flex",
                    flexDirection: "column",
                    boxShadow: "0 24px 64px rgba(0,0,0,0.18)",
                    overflow: "hidden",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* ── Header ─────────────────────────────────────────────── */}
                <div style={{
                    padding: "24px 28px 0",
                    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
                    flexShrink: 0,
                }}>
                    <div>
                        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: "#1a1a2e" }}>
                            Copy Contacts
                        </h3>
                        <p style={{ margin: "4px 0 0", fontSize: 13, color: "#888" }}>
                            Copying into <strong style={{ color: "#555" }}>{targetBook.name}</strong> as independent entries.
                        </p>
                    </div>
                    <button
                        onClick={() => !copying && onClose()}
                        style={{ background: "none", border: "none", fontSize: 22, color: "#aaa", cursor: "pointer", lineHeight: 1, padding: 0 }}
                        aria-label="Close"
                    >
                        ×
                    </button>
                </div>

                {/* ── Full-width book selector ────────────────────────────── */}
                <div style={{ padding: "16px 28px 0", flexShrink: 0 }}>
                    <BookSelector
                        books={sourceBooks}
                        value={selectedSourceId}
                        onChange={handleSourceChange}
                    />
                </div>

                {/* ── Search row: search + select-all + selected count ─────── */}
                <div style={{
                    padding: "10px 28px 12px",
                    display: "flex", alignItems: "center", gap: 8,
                    flexShrink: 0,
                }}>
                    {/* Search */}
                    <div style={{ position: "relative", flex: 1 }}>
                        <SearchIcon style={{
                            position: "absolute", left: 10, top: "50%",
                            transform: "translateY(-50%)", color: "#bbb", fontSize: 16, pointerEvents: "none",
                        }} />
                        <input
                            type="text"
                            placeholder="Search contacts…"
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            disabled={loadingEntries}
                            style={{
                                width: "100%", paddingLeft: 32, paddingRight: 12,
                                paddingTop: 7, paddingBottom: 7,
                                border: "1px solid #e0e0e0", borderRadius: 8,
                                fontSize: 14, outline: "none", background: "#fafafa",
                                boxSizing: "border-box", transition: "border-color 0.15s",
                            }}
                            onFocus={(e) => e.target.style.borderColor = "#1976d2"}
                            onBlur={(e) => e.target.style.borderColor = "#e0e0e0"}
                        />
                    </div>

                    {/* Select-all — small checkbox */}
                    <button
                        type="button"
                        onClick={toggleAll}
                        disabled={filteredEntries.length === 0 || loadingEntries}
                        title={allChecked ? "Deselect all" : "Select all"}
                        style={{
                            width: 20, height: 20, borderRadius: 5, flexShrink: 0,
                            border: `2px solid ${allChecked ? "#1976d2" : someChecked ? "#1976d2" : "#ccc"}`,
                            background: allChecked ? "#1976d2" : "#fff",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            cursor: filteredEntries.length > 0 && !loadingEntries ? "pointer" : "default",
                            transition: "all 0.12s", padding: 0,
                        }}
                    >
                        {allChecked && <CheckIcon sx={{ fontSize: "0.72rem", color: "#fff" }} />}
                        {someChecked && !allChecked && (
                            <div style={{ width: 8, height: 2, background: "#1976d2", borderRadius: 2 }} />
                        )}
                    </button>

                    {/* Selected count — right side */}
                    <span style={{
                        flexShrink: 0, fontSize: 13,
                        color: selectedIds.size > 0 ? "#1976d2" : "#bbb",
                        fontWeight: selectedIds.size > 0 ? 600 : 400,
                        minWidth: 72, textAlign: "right",
                    }}>
                        {selectedIds.size > 0 ? `${selectedIds.size} selected` : "Select all"}
                    </span>
                </div>

                {/* ── Contact list ─────────────────────────────────────────── */}
                <div style={{ flex: 1, overflowY: "auto", padding: "0 28px 8px" }}>
                    {loadingEntries ? (
                        <div style={{ textAlign: "center", padding: "48px 0", color: "#aaa" }}>
                            <i className="fa fa-spinner fa-spin" style={{ fontSize: 24, color: "#1976d2" }} />
                            <p style={{ marginTop: 12, fontSize: 14 }}>Loading contacts…</p>
                        </div>
                    ) : sourceEntries.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "48px 20px", color: "#aaa" }}>
                            <p style={{ fontSize: 15 }}>This book has no phone contacts to copy.</p>
                        </div>
                    ) : filteredEntries.length === 0 ? (
                        <div style={{ textAlign: "center", padding: "36px 0", color: "#aaa" }}>
                            <p style={{ fontSize: 14 }}>No contacts match "{searchQuery.trim()}".</p>
                        </div>
                    ) : (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {filteredEntries.map((entry) => (
                                <ContactCard
                                    key={entry.id}
                                    entry={entry}
                                    checked={selectedIds.has(entry.id)}
                                    onToggle={() => toggleEntry(entry.id)}
                                />
                            ))}
                        </div>
                    )}
                </div>

                {/* ── Classification mismatch warning ──────────────────────── */}
                {mismatchCount > 0 && (
                    <div style={{
                        margin: "0 28px 4px",
                        padding: "10px 14px",
                        background: "#fffbeb",
                        border: "1px solid #fde68a",
                        borderRadius: 8,
                        fontSize: 13,
                        color: "#92400e",
                        display: "flex", alignItems: "center", gap: 8,
                        flexShrink: 0,
                    }}>
                        <span>
                            {mismatchCount} selected contact{mismatchCount !== 1 ? "s keep" : " keeps"} their own
                            classification, which differs from this book
                        </span>
                        <ClassificationBadge value={targetClass} />
                        <span style={{ color: "#a16207" }}>
                            — adjust them manually after copying if needed.
                        </span>
                    </div>
                )}

                {/* ── Footer ──────────────────────────────────────────────── */}
                <div style={{
                    padding: "12px 28px 20px",
                    display: "flex", justifyContent: "flex-end", gap: 10,
                    flexShrink: 0,
                }}>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => !copying && onClose()}
                        disabled={copying}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleCopy}
                        disabled={copying || selectedIds.size === 0}
                    >
                        {copying
                            ? "Copying…"
                            : selectedIds.size > 0
                                ? `Copy ${selectedIds.size} Contact${selectedIds.size !== 1 ? "s" : ""}`
                                : "Copy Contacts"}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default CopyContactsModal;
